import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import TierSection from './components/TierSection';
import SearchBar from './components/SearchBar';
import PokemonProfile from './components/PokemonProfile';
import RaidBossChallenge from './components/RaidBossChallenge';
import {
  trackLoginClick,
  trackLoginSuccess,
  trackLogout,
  trackTierListView,
  trackRaidBossOpen,
  trackRaidBossClose,
} from './utils/analytics';

/** Returns true when viewport width is at or below the given pixel value. Safe when matchMedia is missing (e.g. in tests). */
function useMediaQuery(maxWidthPx) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const handler = (e) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [maxWidthPx]);
  return matches;
}

const TYPE_COLORS = {
  Bug: '#A8B820',
  Dark: '#705848',
  Dragon: '#7038F8',
  Electric: '#F8D030',
  Fairy: '#EE99AC',
  Fighting: '#C03028',
  Fire: '#F08030',
  Flying: '#A890F0',
  Ghost: '#705898',
  Grass: '#78C850',
  Ground: '#E0C068',
  Ice: '#98D8D8',
  Normal: '#A8A878',
  Poison: '#A040A0',
  Psychic: '#F85888',
  Rock: '#B8A038',
  Steel: '#B8B8D0',
  Water: '#6890F0'
};

function App() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently,
  } = useAuth0();
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [tierList, setTierList] = useState(null);
  const [error, setError] = useState(null);
  const [allParties, setAllParties] = useState({});
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  // Derived party for the currently selected type
  const party = allParties[selectedType] || [];
  const setParty = (newParty) => {
    setAllParties(prev => ({
      ...prev,
      [selectedType]: typeof newParty === 'function' ? newParty(prev[selectedType] || []) : newParty
    }));
  };
  const [allTierLists, setAllTierLists] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [scrollTarget, setScrollTarget] = useState(null);
  const [hasLoadedAccountParties, setHasLoadedAccountParties] = useState(false);
  const ALL_TIERS = ['S', 'A+', 'A', 'B+', 'B', 'C+', 'Untiered'];
  const DEFAULT_TIERS = ['S', 'A+', 'A', 'B+', 'B', 'C+'];

  const [selectedTiers, setSelectedTiers] = useState(DEFAULT_TIERS);
  const [pokemonCategory, setPokemonCategory] = useState('all');
  const [showRaidChallenge, setShowRaidChallenge] = useState(false);
  const [raidBossInitial, setRaidBossInitial] = useState(null);
  const isMobileLayout = useMediaQuery(1200);
  const [mobilePartyExpanded, setMobilePartyExpanded] = useState(false);
  const wasAuthenticatedRef = useRef(false);
  const headerBg = TYPE_COLORS[selectedType] || '#6890F0';

  const handleChallengeRaidBoss = (name) => {
    setRaidBossInitial(name);
    setShowRaidChallenge(true);
    trackRaidBossOpen(name || undefined);
  };

  const toggleTier = (tier) => {
    setSelectedTiers(prev =>
      prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]
    );
  };

  const selectAllTiers = () => setSelectedTiers([...ALL_TIERS]);
  const clearAllTiers = () => setSelectedTiers([]);

  const handleInfoHover = (e) => {
    const iconRect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: iconRect.top + iconRect.height / 2,
      left: iconRect.right + 12
    });
  };

  useEffect(() => {
    // Load all tier lists and types
    Promise.all([
      fetch('/data/tier_lists/types.json').then(res => res.json()),
      fetch('/data/tier_lists/all_tier_lists.json').then(res => res.json())
    ])
      .then(([typesData, allData]) => {
        setTypes(typesData);
        setAllTierLists(allData);

        // Load saved parties from localStorage
        let savedParties = {};
        try {
          const raw = localStorage.getItem('pokemonGoAllParties');
          if (raw) savedParties = JSON.parse(raw);
        } catch (err) {
          console.error('Failed to load saved parties:', err);
        }

        // Legacy link-based sync is removed; only load from local storage or account
        const urlParams = new URLSearchParams(window.location.search);
        setAllParties(savedParties);

        // Determine initial type: URL type > saved last type > first type
        const urlType = urlParams.get('type');
        const urlPokemon = urlParams.get('pokemon');
        const savedLastType = localStorage.getItem('pokemonGoLastType');
        const initialType = (urlType && typesData.includes(urlType))
          ? urlType
          : (savedLastType && typesData.includes(savedLastType))
          ? savedLastType
          : typesData[0];
        setSelectedType(initialType);
        if (allData[initialType]) {
          setTierList(allData[initialType]);
        }
        // If navigated from profile page with pokemon param, set up scroll target
        if (urlType && urlPokemon) {
          setScrollTarget(urlPokemon);
          // Find the pokemon's tier in the data and ensure it's visible
          const typeData = allData[initialType];
          if (typeData) {
            for (const cat of ['mega', 'non_mega']) {
              if (!typeData[cat]) continue;
              for (const p of typeData[cat]) {
                if (`${p.pokemon_name}-${p.form}` === urlPokemon && p.tier) {
                  setSelectedTiers(prev => prev.includes(p.tier) ? prev : [...prev, p.tier]);
                  break;
                }
              }
            }
          }
        }
      })
      .catch(err => setError('Failed to load data'));
  }, []);

  // Load parties from the authenticated user's Auth0 profile (user_metadata)
  useEffect(() => {
    if (!isAuthenticated || authLoading || !user || hasLoadedAccountParties) {
      return;
    }

    const loadFromAccount = async () => {
      try {
        const token = await getAccessTokenSilently();
        const domain = process.env.REACT_APP_AUTH0_DOMAIN;
        const response = await fetch(
          `https://${domain}/api/v2/users/${encodeURIComponent(user.sub)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          console.error('Failed to load parties from account:', response.status);
          setHasLoadedAccountParties(true);
          return;
        }

        const data = await response.json();
        const accountParties =
          (data.user_metadata && data.user_metadata.pokemonAllParties) || null;

        if (accountParties && typeof accountParties === 'object') {
          setAllParties(accountParties);
        }
      } catch (err) {
        console.error('Failed to load parties from account:', err);
      } finally {
        setHasLoadedAccountParties(true);
      }
    };

    loadFromAccount();
  }, [
    isAuthenticated,
    authLoading,
    user,
    hasLoadedAccountParties,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    if (selectedType && allTierLists) {
      if (allTierLists[selectedType]) {
        setTierList(allTierLists[selectedType]);
        trackTierListView(selectedType);
      }
    }
  }, [selectedType, allTierLists]);

  // Scroll to target pokemon card when navigated from profile page
  useEffect(() => {
    if (!scrollTarget || !tierList) return;
    const tryScroll = () => {
      const el = document.getElementById(`tier-card-${scrollTarget}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-card');
        setTimeout(() => el.classList.remove('highlight-card'), 2500);
        setScrollTarget(null);
        // Clean up URL params
        setSearchParams({}, { replace: true });
        return true;
      }
      return false;
    };
    // Small delay to let the DOM render
    const timer = setTimeout(tryScroll, 150);
    return () => clearTimeout(timer);
  }, [scrollTarget, tierList, setSearchParams]);

  // Handle SPA navigation with type/pokemon URL params (e.g. from profile page ranking click)
  useEffect(() => {
    if (!allTierLists) return;
    const urlType = searchParams.get('type');
    const urlPokemon = searchParams.get('pokemon');
    if (!urlType || !urlPokemon) return;
    // Already targeting this pokemon, skip
    if (scrollTarget === urlPokemon) return;

    if (types.includes(urlType)) {
      setSelectedType(urlType);
    }
    setScrollTarget(urlPokemon);
    // Ensure the pokemon's tier is visible in filters
    const typeData = allTierLists[urlType];
    if (typeData) {
      for (const cat of ['mega', 'non_mega']) {
        if (!typeData[cat]) continue;
        for (const p of typeData[cat]) {
          if (`${p.pokemon_name}-${p.form}` === urlPokemon && p.tier) {
            setSelectedTiers(prev => prev.includes(p.tier) ? prev : [...prev, p.tier]);
            break;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, allTierLists, types]);

  // Track login success once when user returns from Auth0
  useEffect(() => {
    if (isAuthenticated && !authLoading && !wasAuthenticatedRef.current) {
      trackLoginSuccess();
      wasAuthenticatedRef.current = true;
    }
    if (!isAuthenticated) wasAuthenticatedRef.current = false;
  }, [isAuthenticated, authLoading]);

  // Auto-save selectedType to localStorage
  useEffect(() => {
    if (selectedType) {
      localStorage.setItem('pokemonGoLastType', selectedType);
    }
  }, [selectedType]);

  // Auto-save all parties to localStorage
  useEffect(() => {
    if (Object.keys(allParties).length > 0) {
      localStorage.setItem('pokemonGoAllParties', JSON.stringify(allParties));
    }
  }, [allParties]);

  // Persist parties to the authenticated user's Auth0 profile (user_metadata)
  useEffect(() => {
    if (!isAuthenticated || authLoading || !user) {
      return;
    }
    if (!allParties || Object.keys(allParties).length === 0) {
      return;
    }

    const saveToAccount = async () => {
      try {
        const token = await getAccessTokenSilently();
        const domain = process.env.REACT_APP_AUTH0_DOMAIN;
        await fetch(
          `https://${domain}/api/v2/users/${encodeURIComponent(user.sub)}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_metadata: {
                pokemonAllParties: allParties,
              },
            }),
          }
        );
      } catch (err) {
        console.error('Failed to save parties to account:', err);
      }
    };

    saveToAccount();
  }, [allParties, isAuthenticated, authLoading, user, getAccessTokenSilently]);

  const handleTypeChange = (newType) => {
    setSelectedType(newType);
  };

  const getPokemonId = (pokemon) => {
    return `${pokemon.pokemon_name}-${pokemon.form}-${pokemon.fast_move}-${pokemon.charged_move}`;
  };

  const addToParty = (pokemon) => {
    const isMega = pokemon.pokemon_name.includes('Mega');
    const existingMega = party.find(p => p.pokemon_name.includes('Mega'));
    const isAlreadyInParty = party.find(p => getPokemonId(p) === getPokemonId(pokemon));

    // For Mega: if clicking on already selected mega, remove it
    if (isMega && isAlreadyInParty) {
      removeFromParty(pokemon);
      return;
    }

    // For Mega: if clicking on a different mega when one is already selected, replace it
    if (isMega && existingMega) {
      setParty(party.map(p => 
        getPokemonId(p) === getPokemonId(existingMega) ? pokemon : p
      ));
      return;
    }

    // For non-Mega: allow duplicates up to party size limit
    if (party.length >= 6) {
      alert('Party is full! Maximum 6 Pokémon allowed.');
      return;
    }

    // Add the pokemon (allows duplicates for non-mega)
    setParty([...party, pokemon]);
  };

  const removeFromParty = (pokemon) => {
    // Remove only the first occurrence
    const index = party.findIndex(p => getPokemonId(p) === getPokemonId(pokemon));
    if (index > -1) {
      const newParty = [...party];
      newParty.splice(index, 1);
      setParty(newParty);
    }
  };

  const removeAllCopies = (pokemon) => {
    setParty(party.filter(p => getPokemonId(p) !== getPokemonId(pokemon)));
  };

  const clearParty = () => {
    setParty([]);
  };

  const categorizePokemon = (pokemon) => {
    if (pokemon.pokemon_name.includes('Mega')) {
      return 'mega';
    }
    if (pokemon.is_legendary_or_mythical) {
      return 'legendary';
    }
    return 'other';
  };

  // Group party by unique pokemon and count duplicates
  const groupedParty = party.reduce((acc, pokemon) => {
    const id = getPokemonId(pokemon);
    if (!acc[id]) {
      acc[id] = { pokemon, count: 0 };
    }
    acc[id].count++;
    return acc;
  }, {});

  const partyByCategory = {
    mega: Object.values(groupedParty).filter(({ pokemon }) => categorizePokemon(pokemon) === 'mega'),
    legendary: Object.values(groupedParty).filter(({ pokemon }) => categorizePokemon(pokemon) === 'legendary'),
    other: Object.values(groupedParty).filter(({ pokemon }) => categorizePokemon(pokemon) === 'other')
  };

  // Calculate average party DPS (L50 only)
  const calculateAverageDPS = () => {
    if (party.length === 0) return { l50: 0 };
    const totalL50 = party.reduce((sum, p) => sum + parseFloat(p.combined_effective_dps || 0), 0);
    return { l50: (totalL50 / party.length).toFixed(1) };
  };

  const averageDPS = calculateAverageDPS();

const tierListPage = (
    <>
      <main className="main">
            <div className="filters-bar">
                <div className="type-selector">
                    <label htmlFor="type-select">Type:</label>
                    <select 
                        id="type-select"
                        value={selectedType} 
                        onChange={(e) => handleTypeChange(e.target.value)}
                    >
                        {types.map(type => {
                            const count = (allParties[type] || []).length;
                            return <option key={type} value={type}>{type}{count > 0 ? ` (Party: ${count} of 6)` : ''}</option>;
                        })}
                    </select>
                </div>
                <div className="tier-filter">
                    <label>Tiers:</label>
                    <div className="tier-filter-options">
                        {ALL_TIERS.map(tier => (
                            <button
                                key={tier}
                                className={`tier-filter-btn tier-filter-btn-${tier.replace('+', '-plus')} ${selectedTiers.includes(tier) ? 'active' : ''}`}
                                onClick={() => toggleTier(tier)}
                            >
                                {tier}
                            </button>
                        ))}
                        <button className="tier-filter-toggle" onClick={selectedTiers.length === ALL_TIERS.length ? clearAllTiers : selectAllTiers}>
                            {selectedTiers.length === ALL_TIERS.length ? 'Clear' : 'All'}
                        </button>
                    </div>
                </div>
                <div className="filters-bar-actions">
                    <button
                      className="button raid-boss-btn"
                      onClick={() => {
                        setShowRaidChallenge(true);
                        trackRaidBossOpen();
                      }}
                    >
                        Simulate Raid Boss
                    </button>
                </div>
            </div>
            <div className="content-layout">
                <div className="tier-lists">
                    {error && <div className="error">{error}</div>}
                    {!allTierLists && !error && <div className="loading">Loading...</div>}
                    
                    {tierList && allTierLists && (
                        <>
                            <TierSection 
                                title="Top Mega Pokémon" 
                                pokemons={tierList.mega} 
                                onPokemonClick={addToParty}
                                selectedPokemon={party}
                                getPokemonId={getPokemonId}
                                groupedParty={groupedParty}
                                selectedTiers={selectedTiers}
                                defaultCollapsed={party.some(p => p.pokemon_name.includes('Mega'))}
                            />
                            <TierSection 
                                title="Top Non-Mega Pokémon" 
                                pokemons={(tierList.non_mega || []).map(p => ({ ...p, isLegendary: !!p.is_legendary_or_mythical }))}
                                onPokemonClick={addToParty}
                                selectedPokemon={party}
                                getPokemonId={getPokemonId}
                                groupedParty={groupedParty}
                                selectedTiers={selectedTiers}
                                categoryFilter={pokemonCategory}
                                onCategoryChange={setPokemonCategory}
                            />
                        </>
                    )}
                </div>

                {isMobileLayout && !mobilePartyExpanded && (
                    <button
                      type="button"
                      className="party-mobile-bar"
                      onClick={() => setMobilePartyExpanded(true)}
                      aria-label={`Your party has ${party.length} of 6 Pokémon. Tap to open party.`}
                    >
                      <span className="party-mobile-bar-label">Your Party ({party.length}/6)</span>
                      <span className="party-mobile-bar-chevron" aria-hidden="true">&#9650;</span>
                    </button>
                )}
                <aside
                  className={`party-panel ${isMobileLayout ? (mobilePartyExpanded ? 'party-panel-mobile-expanded' : 'party-panel-mobile-collapsed') : ''}`}
                >
                    {isMobileLayout && mobilePartyExpanded && (
                        <button
                          type="button"
                          className="party-panel-collapse-btn"
                          onClick={() => setMobilePartyExpanded(false)}
                          aria-label="Collapse party"
                        >
                          <span aria-hidden="true">&#9660;</span> Collapse
                        </button>
                    )}
                    <div className="party-header">
                        <div className="party-title-row">
                            <h2>Your Party ({party.length}/6)</h2>
                            <div className="info-icon-wrapper" onMouseEnter={handleInfoHover}>
                                <span className="info-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg></span>
                                <div 
                                    className="info-tooltip"
                                    style={{
                                        top: `${tooltipPosition.top}px`,
                                        left: `${tooltipPosition.left}px`,
                                        transform: 'translateY(-50%)'
                                    }}
                                >
                                    <div className="tooltip-title">Party Rules</div>
                                    <ul>
                                        <li>Maximum 6 Pokémon</li>
                                        <li>Only 1 Mega Evolution</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {party.length === 0 ? (
                        <div className="party-empty">
                            <p>Click "+ Party" on a Pokémon card to add it to your party!</p>
                        </div>
                    ) : (
                        <>
                            {partyByCategory.mega.length > 0 && (
                                <div className="party-section">
                                    <h3>Mega Evolution</h3>
                                    <div className="party-pokemon-list">
                                        {partyByCategory.mega.map(({ pokemon, count }) => (
                                            <div key={getPokemonId(pokemon)} className="party-pokemon-item-with-controls">
                                                <div className="party-pokemon-info">
                                                    {pokemon.icon_filename && (
                                                        <img
                                                            src={`/pokemon_icons/${pokemon.icon_filename}`}
                                                            alt=""
                                                            className="party-pokemon-icon"
                                                        />
                                                    )}
                                                    <span className="party-pokemon-name">{pokemon.pokemon_name}</span>
                                                    <span className="party-remove" onClick={() => removeAllCopies(pokemon)}>✕</span>
                                                </div>
                                                <div className="party-pokemon-dps">
                                                    <span className="party-dps-label">L50</span>
                                                    <span className="party-dps-value">{Number(pokemon.combined_effective_dps).toFixed(1)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {partyByCategory.legendary.length > 0 && (
                                <div className="party-section">
                                    <h3>Legendary/Mythical</h3>
                                    <div className="party-pokemon-list">
                                        {partyByCategory.legendary.map(({ pokemon, count }) => (
                                            <div key={getPokemonId(pokemon)} className="party-pokemon-item-with-controls">
                                                <div className="party-pokemon-info">
                                                    {pokemon.icon_filename && (
                                                        <img
                                                            src={`/pokemon_icons/${pokemon.icon_filename}`}
                                                            alt=""
                                                            className="party-pokemon-icon"
                                                        />
                                                    )}
                                                    <span className="party-pokemon-name">{pokemon.pokemon_name}</span>
                                                    <div className="party-controls">
                                                        <button className="party-control-btn" onClick={() => removeFromParty(pokemon)}>−</button>
                                                        <span className="party-count">{count}</span>
                                                        <button className="party-control-btn" onClick={() => addToParty(pokemon)}>＋</button>
                                                        <span className="party-remove-all" onClick={() => removeAllCopies(pokemon)}>✕</span>
                                                    </div>
                                                </div>
                                                <div className="party-pokemon-dps">
                                                    <span className="party-dps-label">L50</span>
                                                    <span className="party-dps-value">{Number(pokemon.combined_effective_dps).toFixed(1)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {partyByCategory.other.length > 0 && (
                                <div className="party-section">
                                    <h3>Other Pokémon</h3>
                                    <div className="party-pokemon-list">
                                        {partyByCategory.other.map(({ pokemon, count }) => (
                                            <div key={getPokemonId(pokemon)} className="party-pokemon-item-with-controls">
                                                <div className="party-pokemon-info">
                                                    {pokemon.icon_filename && (
                                                        <img
                                                            src={`/pokemon_icons/${pokemon.icon_filename}`}
                                                            alt=""
                                                            className="party-pokemon-icon"
                                                        />
                                                    )}
                                                    <span className="party-pokemon-name">{pokemon.pokemon_name}</span>
                                                    <div className="party-controls">
                                                        <button className="party-control-btn" onClick={() => removeFromParty(pokemon)}>−</button>
                                                        <span className="party-count">{count}</span>
                                                        <button className="party-control-btn" onClick={() => addToParty(pokemon)}>＋</button>
                                                        <span className="party-remove-all" onClick={() => removeAllCopies(pokemon)}>✕</span>
                                                    </div>
                                                </div>
                                                <div className="party-pokemon-dps">
                                                    <span className="party-dps-label">L50</span>
                                                    <span className="party-dps-value">{Number(pokemon.combined_effective_dps).toFixed(1)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {party.length > 0 && (
                        <div className="party-footer">
                            <div className="party-stats">
                                <div className="party-stats-title">Average Party DPS</div>
                                <div className="party-stats-row">
                                    <div className="party-stat-item">
                                        <span className="party-stat-label">Level 50</span>
                                        <span className="party-stat-value">{averageDPS.l50}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="party-actions">
                                <button className="button button-clear" onClick={clearParty}>Clear Party</button>
                            </div>
                        </div>
                    )}
                </aside>
            </div>
      </main>
    </>
  );

return (
    <div className="app">
        <header className="header" style={{ backgroundColor: headerBg }}>
          <div className="header-row">
            {!isMobileLayout && (
              <Link to="/" className="header-title-link">
                <h1>Pokémon GO Type Tier Lists</h1>
              </Link>
            )}
            <div className="header-controls">
              <SearchBar allTierLists={allTierLists} />
              <div className="auth-controls">
                {authLoading ? (
                  <span className="auth-status">Checking login…</span>
                ) : isAuthenticated ? (
                  <>
                    {user?.picture && (
                      <img
                        src={user.picture}
                        alt={user?.name || user?.nickname || 'Profile picture'}
                        className="auth-avatar"
                      />
                    )}
                    <button
                      type="button"
                      className="button button-secondary auth-button"
                      onClick={() => {
                        trackLogout();
                        logout({
                          logoutParams: { returnTo: window.location.origin },
                        });
                      }}
                    >
                      Log out
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="button button-primary auth-button"
                    onClick={() => {
                      trackLoginClick();
                      loginWithRedirect({
                        appState: {
                          returnTo: `${window.location.pathname}${window.location.search}`,
                        },
                      });
                    }}
                  >
                    Log in
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        <Routes>
          <Route path="/" element={tierListPage} />
          <Route path="/pokemon/:pokemonName" element={<PokemonProfile allTierLists={allTierLists} onChallengeRaidBoss={handleChallengeRaidBoss} />} />
        </Routes>

        {showRaidChallenge && (
          <RaidBossChallenge
            allParties={allParties}
            allTierLists={allTierLists}
            initialBoss={raidBossInitial}
            onClose={() => {
              trackRaidBossClose();
              setShowRaidChallenge(false);
              setRaidBossInitial(null);
            }}
          />
        )}

        <footer className="footer">
            <p>
                Pokémon and Pokémon GO are copyright of The Pokémon Company, Niantic, Inc., and Nintendo. All trademarked images and names are property of their respective owners, and any such material is used on this site for educational purposes only. Solver d.o.o. has no affiliation with The Pokémon Company, Niantic, Inc., or Nintendo. This site is not sponsored, endorsed, or affiliated with Niantic, Inc. or The Pokémon Company in any way. All data provided on this site is for informational purposes only.
            </p>
            <p>
                Foundational data is graciously supplied by <a href="https://pogoapi.net/" target="_blank" rel="noopener noreferrer">https://pogoapi.net/</a>
            </p>
        </footer>
    </div>
);
}

export default App;
